import {
  getWorkflowFromValues,
  RECEIVING_WORKFLOW_ONSITE_CAPTURE,
} from "../../modals/ProjectActions/projectReceivingWorkflow";

const IN_PROGRESS_NOTIFICATION_START_HOUR = 7;
const IN_PROGRESS_NOTIFICATION_END_HOUR = 19;
const IN_PROGRESS_NOTIFICATION_TIME_OPTIONS = Array.from(
  { length: IN_PROGRESS_NOTIFICATION_END_HOUR - IN_PROGRESS_NOTIFICATION_START_HOUR + 1 },
  (_, index) => {
    const hour = IN_PROGRESS_NOTIFICATION_START_HOUR + index;
    const date = new Date(Date.UTC(2000, 0, 1, hour, 0, 0));
    return {
      value: `${String(hour).padStart(2, '0')}:00`,
      label: date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: 'UTC'
      })
    };
  }
);

export const getCustomerToggles = (settings, isAdminOrManager, costCenters = [], projectValues = {}) => {
  const categories = [];
  const inProgressNotificationsEnabled = !!projectValues?.settings?.in_progress_shipment_notifications?.enabled;
  const isOnsiteCaptureMode = getWorkflowFromValues(projectValues) === RECEIVING_WORKFLOW_ONSITE_CAPTURE;
  const onsiteInventoryCaptureToggle = {
    field: "settings.onsite_inventory_capture",
    label: "Use Origin Quantity in Store",
    description: "Origin quantity is used to display the quantity shown available in the Store.",
  };

  // Receiving & Item Setup
  if (settings.receiving_wizard) {
    categories.push({
      category: "Receiving & Item Setup",
      options: [
        {
          field: 'referenceAssets',
          label: 'Create New Items from SKU List',
          description: 'Allow staff to choose from the SKU library.\n\nEach time a SKU is selected, a new item is always created. This results in multiple items for the same SKU.\n\nSeparate items are necessary for different expiration dates, conditions, batch numbers, etc.'
        },
        {
          field: 'importedAssets',
          label: 'Restock Items and SKUs',
          description: 'Allow staff to select items that already exist in inventory or choose from the SKU library.\n\nIf an item already exists, staff restock that exact item, ensuring there is only one item per SKU.'
        },
        {
          field: 'addPreviouslyReceivedAssetsToShipments',
          label: 'Receive Returned Items',
          description: 'Allow staff to add items that were previously received.\n\nThis helps when items are returned from the field and staff don’t want to retake photos or re-enter item details.'
        },
        {
          field: 'minimumShipmentImages',
          label: 'Minimum Shipment Pictures',
          description: 'Require staff to upload a minimum number of images in the first step of the receiving process.\n\nTypically, receiving documentation such as BOL, packing slips, etc.',
          type: 'number'
        },
        {
          field: 'minimumAssetImages',
          label: 'Minimum Item Pictures',
          description: 'Require warehouse staff to upload a minimum number of images per item during receiving',
          type: 'number'
        },
        ...(settings.reservations && settings.receiving_wizard ? [{
          field: "settings.add_inbounds_to_reservations",
          label: "Add Inbounds to Reservations",
          description: "Insert a reservation step into the receving process so staff can route received items into a reservation.",
        }] : []),
      ]
    });
  }

  // Billing
  if (settings.receiving_wizard) {
    const billingOptions = [
      {
        field: 'billing',
        label: 'Billing Engine',
        description: 'Enable billing engine to calculate fees for inbound, storage, and outbound activity.'
      }
    ];

    // Only show Additional Ancillary Charges Step if additional_billing_wizard_steps is false
    if (!settings.additional_billing_wizard_steps) {
      billingOptions.push({
        field: 'receivingWizardBillingStep',
        label: 'Extra Step for Ancillary Charges',
        description: 'Insert an additional step in the receiving and outbound workflow that prompts warehouse staff to record any extra charges they incurred.'
      });
    }

    billingOptions.push({
      field: 'settings.time_increment_minutes',
      label: 'Time Increment',
      description: 'Choose the time increment used when entering billable time for time-based charges.',
      type: 'select',
      choices: [
        { value: '60', label: '60 Minutes' },
        { value: '30', label: '30 Minutes' },
        { value: '20', label: '20 Minutes' },
        { value: '15', label: '15 Minutes' }
      ]
    });

    categories.push({
      category: "Billing",
      options: billingOptions
    });
  }

  // Item Grouping & Kits
  const itemGroupingOptions = [];

  if (settings.kitting) {
    itemGroupingOptions.push({
      field: 'kitting',
      label: 'Kitting',
      description: 'Group items into kits to simplify ordering, inventory management, and fulfillment'
    });
  }

  itemGroupingOptions.push({
    field: 'groupedAssets',
    label: 'Grouped Items',
    description: 'Create grouped items from SKUs for simplified shopping and warehouse fulfillment.'
  });

  itemGroupingOptions.push({
    field: 'groupedSerializedAssets',
    label: 'Grouped Items (Serialized)',
    description: 'Create serialized grouped items from SKUs and require warehouse staff to manually assign serialized items during fulfillment.'
  });

  if (projectValues.groupedAssets || projectValues.groupedSerializedAssets) {
    itemGroupingOptions.push({
      field: 'settings.include_regular_assets_from_grouped_projects',
      label: 'Show Individual Items in Shopping',
      description: 'Also show regular item records in the shopping store for grouped projects.',
      nested: true
    });
  }

  if (itemGroupingOptions.length > 0) {
    categories.push({
      category: "Item Grouping & Kits",
      options: itemGroupingOptions
    });
  }

  // Email Notifications
  const emailNotificationOptions = [];

  emailNotificationOptions.push({
    field: 'settings.in_progress_shipment_notifications.enabled',
    label: 'In-Progress Shipment Notifications',
    description: 'Send reminder emails for shipments that have started, are still missing a complete time, and already include at least one shipment attachment.'
  });

  emailNotificationOptions.push({
    field: 'settings.include_excel_attachment_in_email_notifications',
    label: 'Include Excel Attachments in Notification Emails',
    description: 'Attach an Excel export to shipment, outbound order, and internal work order notification emails using the same table data shown in the PDF.'
  });

  if (inProgressNotificationsEnabled) {
    emailNotificationOptions.push({
      field: 'settings.in_progress_shipment_notifications.schedule',
      type: 'sub-group',
      options: [
        {
          field: 'settings.in_progress_shipment_notifications.times',
          label: 'Notification Times',
          description: 'Choose the top-of-hour times (company timezone) when reminder emails should be sent. The hourly rake task will still send if it runs a few minutes late.',
          type: 'multi-select',
          choices: IN_PROGRESS_NOTIFICATION_TIME_OPTIONS
        }
      ]
    });
  }

  if (emailNotificationOptions.length > 0) {
    categories.push({
      category: "Email Notifications",
      options: emailNotificationOptions
    });
  }

  // Outbound Order Controls
  const outboundOrderControlOptions = [];

  outboundOrderControlOptions.push({
    field: 'settings.requires_outbound_tracking_for_completion',
    label: 'Require Tracking Before Completing Outbound Orders',
    description: 'Keep outbound orders open until at least one tracking number is saved. Once all items are picked and tracking exists, the order completes and sends the final customer email.'
  });
  
  if (settings.inventory_tracking) {
    outboundOrderControlOptions.push({
      field: 'allowExternalUsersToEditOrders',
      label: 'Allow External Users to Edit Orders',
      description: 'Enable external users to modify and update their submitted outbound orders'
    });
  }

  // outboundOrderControlOptions.push({
  //   field: 'settings.cut_off',
  //   label: `${settings.filter_field2} Cutoff Window`,
  //   description: `Set the cutoff window in hours.`,
  //   type: 'number'
  // });

  if (outboundOrderControlOptions.length > 0) {
    categories.push({
      category: "Outbound Order Controls",
      options: outboundOrderControlOptions
    });
  }

  // Internal Work Orders
  const internalWorkOrderOptions = [];

  if (settings.internal_work_orders) {
    internalWorkOrderOptions.push({
      field: 'allowExternalUsersToCreateInternalOrders',
      label: 'Allow External Users to Create Internal Orders',
      description: 'Permit external stakeholders to submit internal work orders from their portal'
    });
  }

  if (internalWorkOrderOptions.length > 0) {
    categories.push({
      category: "Internal Work Orders",
      options: internalWorkOrderOptions
    });
  }

  // Customer Visibility & Access
  const customerVisibilityAccessOptions = [
    ...(costCenters.length > 1 ? [{
      field: 'settings.cost_center_user_restrictions',
      label: 'Cost Center User Restrictions',
      description: 'Enable cost center access controls when inviting users to this customer'
    }] : []),
    ...(settings.inventory_tracking ? [{
      field: 'settings.display_in_store',
      label: 'Display in Store',
      description: 'Show this customer\'s inventory in the shopping portal'
    }] : []),
    ...(isOnsiteCaptureMode ? [onsiteInventoryCaptureToggle] : []),
    ...(isAdminOrManager ? [{
      field: 'active',
      label: 'Active',
      description: 'Mark this customer as active so they appear in searches and the assets page. Inactive customers remain available in settings'
    }] : [])
  ];

  categories.push({
    category: "Customer Visibility & Access",
    options: customerVisibilityAccessOptions
  });

  return categories;
};
